// Adapted from ReactBits (https://reactbits.dev) — MIT License
// Infinite horizontal marquee with pause-on-hover and reverse direction
import { useRef, useState, useEffect } from 'react';

export default function Marquee({
  children,
  speed = 30,         // px per second
  direction = 'left', // 'left' or 'right'
  pauseOnHover = true,
  gap = 16,           // px gap between repeated items
  className = '',
}) {
  const trackRef = useRef(null);
  const [trackWidth, setTrackWidth] = useState(0);

  useEffect(() => {
    if (!trackRef.current) return;
    const measure = () => {
      const firstSet = trackRef.current?.firstElementChild;
      if (firstSet) setTrackWidth(firstSet.offsetWidth + gap);
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(measure)
      : null;
    if (ro && trackRef.current) ro.observe(trackRef.current);
    return () => ro?.disconnect();
  }, [children, gap]);

  const duration = trackWidth > 0 ? trackWidth / speed : 20;
  const reverse = direction === 'right';

  return (
    <div
      className={className}
      style={{
        overflow: 'hidden',
        width: '100%',
        position: 'relative',
        maskImage: 'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)',
      }}
    >
      <div
        ref={trackRef}
        className="marquee-track"
        style={{
          display: 'flex',
          width: 'max-content',
          gap: `${gap}px`,
          animationName: 'marquee-scroll',
          animationDuration: `${duration}s`,
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
          animationDirection: reverse ? 'reverse' : 'normal',
          willChange: 'transform',
        }}
        onMouseEnter={(e) => { if (pauseOnHover) e.currentTarget.style.animationPlayState = 'paused'; }}
        onMouseLeave={(e) => { if (pauseOnHover) e.currentTarget.style.animationPlayState = 'running'; }}
      >
        {/* Two copies for seamless loop */}
        <div style={{ display: 'flex', gap: `${gap}px`, flexShrink: 0 }}>{children}</div>
        <div style={{ display: 'flex', gap: `${gap}px`, flexShrink: 0 }} aria-hidden="true">{children}</div>
      </div>

      <style>{`
        @keyframes marquee-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
